import { Col, Descriptions, Empty, Image, Row, Space, Switch, Table, Typography } from 'antd';
import { AiOutlineInfoCircle } from 'react-icons/ai';
import { BsBox2, BsImages } from 'react-icons/bs';

import ProductImage from '../../components/productImage';
import { sizeEnumDictionary, weightEnumDictionary } from '../../utils';
import { convertProductVolume, convertProductWeight, formatPrice } from '../../utils/format';
import { descriptionProps, val, YesNo } from './components/descriptions';
import SectionCard from './components/SectionCard';

const { Text } = Typography;

/** Гол зурагны дээд хэмжээ (дөрвөлжин тул өргөн, өндөр нь ижил) */
const COVER_MAX_SIZE = 260;

/** Нэмэлт зурагны хэмжээ */
const THUMB_SIZE = 64;

/** Зургийн нүд: өргөн, өндөр нь үргэлж ижил (дөрвөлжин) */
const SquareImage = ({ src, alt = 'Барааны зураг', size, radius = 8 }) => (
  <div
    style={{
      width: size ?? '100%',
      maxWidth: '100%',
      aspectRatio: '1 / 1',
      border: '1px solid #f0f0f0',
      borderRadius: radius,
      background: '#fafafa',
      overflow: 'hidden',
    }}
  >
    <ProductImage
      alt={alt}
      src={src}
      size="100%"
      iconSize={size ? 28 : 64}
      preview
      style={{ objectFit: 'contain', display: 'block', borderRadius: 0 }}
      wrapperStyle={{ display: 'block', width: '100%', height: '100%' }}
    />
  </div>
);

/**
 * Нэгж барааны дэлгэрэнгүй мэдээлэл.
 * Захиалгын дэлгэрэнгүй дээрхтэй ижил Card + Descriptions төрх.
 */
const ProductDetailInfo = ({ detail = {}, checked = false, toggling = false, onToggleActive }) => {
  const cover = detail?.imageCover;
  const gallery = [detail?.imageCover, ...(detail?.images ?? [])].filter(Boolean);

  const hasRange = Boolean(detail?.minValue && detail?.maxValue);
  const hasReorder = Boolean(detail?.reorderQuantity && detail?.minStock);

  /** Ерөнхий мэдээллийн талбарууд */
  const infoItems = [
    {
      key: 'volume',
      label: 'Эзлэхүүн',
      children: (
        <span style={{ textTransform: 'lowercase' }}>
          {convertProductVolume(detail?.volume || 0, detail?.sizeMeasure)}
          {sizeEnumDictionary[detail?.sizeMeasure] ?? 'см'}
          <sup>3</sup>
        </span>
      ),
    },
    {
      key: 'weight',
      label: 'Жин',
      children: (
        <span style={{ textTransform: 'lowercase' }}>
          {convertProductWeight(detail?.weight || 0, detail?.weightMeasure)}
          {weightEnumDictionary[detail?.weightMeasure] ?? 'г'}
        </span>
      ),
    },
    { key: 'color', label: 'Өнгө', children: val(detail?.attributes?.color) },
    { key: 'type', label: 'Төрөл', children: val(detail?.attributes?.type) },
    { key: 'size', label: 'Хэмжээ', children: val(detail?.attributes?.size) },
    { key: 'category', label: 'Категори', children: val(detail?.category) },
    {
      key: 'returnable',
      label: 'Буцаагдах эсэх',
      children: <YesNo value={detail?.isReturnable} />,
    },
    {
      key: 'expire',
      label: 'Дуусах хугацаатай эсэх',
      children: <YesNo value={detail?.willExpire} />,
    },
    {
      key: 'isFragile',
      label: 'Эвдрэх аюултай эсэх',
      children: <YesNo value={detail?.isFragile} />,
    },
    ...(hasRange
      ? [{ key: 'range', label: 'Утгын хязгаар', children: `${detail.minValue} - ${detail.maxValue}` }]
      : []),
    ...(detail?.price
      ? [
          {
            key: 'price',
            label: 'Үнэ',
            // detail.price нь ҮРГЭЛЖ үндсэн үнэ. Хямдрал хүчинтэй эсэхийг болон
            // хямдарсан үнийг backend бодож өгдөг (saleActive / salePrice) —
            // хугацаа дуусмагц saleActive false болж үндсэн үнэ л харагдана.
            children:
              detail?.saleActive && detail?.salePrice ? (
                <Space size={8} wrap>
                  <Text delete type="secondary">
                    {formatPrice(detail.price)}
                  </Text>
                  <Text strong>{formatPrice(detail.salePrice)}</Text>
                </Space>
              ) : (
                <Text strong>{formatPrice(detail.price)}</Text>
              ),
          },
        ]
      : []),
    ...(hasReorder
      ? [
          {
            key: 'minStock',
            label: 'Зайлшгүй байх нөөц',
            children: val(detail?.minStock),
          },
          {
            key: 'reorderQuantity',
            label: 'Дахин захиалга хийх тоо хэмжээ',
            children: val(detail?.reorderQuantity),
          },
        ]
      : []),
    /*
     * Барааны тайлбар нь бараа үүсгэхэд ЗААВАЛ бөглөх талбар тул мерчант бүрд
     * харагдана. Өмнө нь зөвхөн Эрээний (isErlian) мерчантад харуулдаг байсан
     * тул ердийн мерчант оруулсан тайлбараа дэлгэрэнгүйгээс огт олохгүй,
     * зөвхөн өөрчлөлтийн хүсэлтийн харьцуулалтаас л хардаг байв.
     */
    {
      key: 'description',
      label: 'Барааны тайлбар',
      span: 3,
      children: val(detail?.description),
    },
  ];

  /** Агуулах бүрийн үлдэгдэл */
  const stockColumns = [
    {
      key: 'warehouse',
      title: 'Агуулах',
      render: (_, record) => val(record?._id),
    },
    {
      key: 'stock',
      title: 'Үлдэгдэл',
      align: 'right',
      width: 160,
      render: (_, record) => <Text strong>{record?.totalStock ?? 0}</Text>,
    },
  ];

  const stockSummary = (rows = []) => (
    <Table.Summary.Row>
      <Table.Summary.Cell>
        <Text type="secondary">Нийт</Text>
      </Table.Summary.Cell>
      <Table.Summary.Cell align="right">
        <Text strong>{rows.reduce((total, row) => total + (Number(row?.totalStock) || 0), 0)}</Text>
      </Table.Summary.Cell>
    </Table.Summary.Row>
  );

  return (
    <Space direction="vertical" size={16} style={{ width: '100%', padding: 16 }}>
      <SectionCard
        icon={<BsBox2 />}
        title="Барааны мэдээлэл"
        action={
          <Space size={8}>
            <Text type="secondary">Идэвхтэй эсэх</Text>
            <Switch checked={checked} loading={toggling} onChange={onToggleActive} />
          </Space>
        }
      >
        <Descriptions
          {...descriptionProps}
          column={{ xs: 1, sm: 3 }}
          items={[
            { key: 'title', label: 'Барааны нэр', children: val(detail?.title) },
            { key: 'sku', label: 'SKU', children: val(detail?.baseSku) },
            {
              key: 'barcode',
              label: 'Баркод',
              children: val(detail?.barCodes?.length ? detail.barCodes.join(', ') : null),
            },
          ]}
        />
      </SectionCard>

      <Row gutter={[16, 16]}>
        {/* Col-ыг flex болгосон тул доторх карт мөрийн өндрөөр сунаж, хоёр карт ижил өндөртэй болно */}
        <Col xs={24} xl={4} style={{ display: 'flex' }}>
          <SectionCard
            icon={<BsImages />}
            title="Барааны зураг"
            style={{ width: '100%', display: 'flex', flexDirection: 'column' }}
            styles={{
              body: { flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' },
            }}
          >
            <Image.PreviewGroup>
              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <div style={{ width: '100%', maxWidth: COVER_MAX_SIZE, margin: '0 auto' }}>
                  <SquareImage src={cover} alt="Барааны үндсэн зураг" />
                </div>
                {gallery.length > 1 && (
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 8,
                      justifyContent: 'center',
                    }}
                  >
                    {gallery.slice(1).map((image, index) => (
                      <SquareImage key={index} src={image} size={THUMB_SIZE} radius={6} />
                    ))}
                  </div>
                )}
              </Space>
            </Image.PreviewGroup>
          </SectionCard>
        </Col>

        <Col xs={24} xl={20} style={{ display: 'flex' }}>
          <SectionCard
            icon={<AiOutlineInfoCircle />}
            title="Ерөнхий мэдээлэл"
            style={{ width: '100%' }}
          >
            <Descriptions
              {...descriptionProps}
              column={{ xs: 1, sm: 2, xl: 3 }}
              items={infoItems}
            />
          </SectionCard>
        </Col>
      </Row>

      <SectionCard
        icon={<BsBox2 />}
        title="Үлдэгдэл"
        styles={{ body: { padding: detail?.inventoryStock?.length ? 0 : undefined } }}
      >
        {detail?.inventoryStock?.length > 0 ? (
          <Table
            className="product__stock__table"
            size="small"
            columns={stockColumns}
            dataSource={detail.inventoryStock}
            rowKey={(record, index) => record?._id ?? index}
            pagination={false}
            summary={stockSummary}
            scroll={{ x: 'max-content' }}
          />
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Үлдэгдэл байхгүй байна."
            style={{ margin: 0 }}
          />
        )}
      </SectionCard>
    </Space>
  );
};

export default ProductDetailInfo;
